import { useState, useMemo, ReactElement } from "react";

import Table from "~frontend/components/Table";
import { formatDateTime } from "~frontend/util/dateFormat";

import { CampaignForView } from "./types";

export type CampaignEvent = CampaignForView["events"][number];

export default function CampaignEventsTable({
  campaign,
}: {
  campaign: CampaignForView;
}): ReactElement {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const sortedEvents = useMemo(
    () => campaign.events.slice(0).sort((a, b) => b.createdAt - a.createdAt),
    [campaign.events]
  );

  function renderEventMessage(event: CampaignEvent) {
    switch (event.__typename) {
      case "CampaignInitialSendBeginEvent":
        if (event.data.partialSendQty != null) {
          return `Started partial send to ${event.data.partialSendQty.toLocaleString()} contacts.`;
        } else {
          return `Started full send.`;
        }
      case "CampaignInitialSendScheduleEvent":
        if (event.data.partialSendQty != null) {
          return `Scheduled partial send to ${event.data.partialSendQty.toLocaleString()} contacts.`;
        } else {
          return `Scheduled full send for ${formatDateTime(
            event.data.scheduledFor
          )}.`;
        }
      case "CampaignInitialSendBeginScheduledEvent":
        return "Started scheduled send.";
      case "CampaignInitialSendCancelManualEvent":
        return `Send canceled by ${event.data.canceledByUserFullName}.`;
      case "CampaignInitialSendCancelTextingHoursEvent":
        return "Send canceled due to texting hours.";
      case "CampaignInitialSendCancelErrorEvent":
        return `Send canceled due to error: ${event.data.errorMessage}`;
      case "CampaignInitialSendCompletePartialEvent":
        return `Partial send completed. ${event.data.totalSent.toLocaleString()} total initial messages sent.`;
      case "CampaignInitialSendCompleteFullEvent":
        return "Initial message sending completed.";
      case "CampaignArchivedEvent":
        return `Campaign archived by ${event.data.archivedByUserFullName}.`;
      case "CampaignUnarchivedEvent":
        return `Campaign unarchived by ${event.data.unarchivedByUserFullName}.`;
      case "CampaignInitialSendCancelErrorRateEvent":
        return `Send canceled due to elevated error rate (${Math.round(
          event.data.errorRate * 100
        )}% since ${formatDateTime(event.data.since)}).`;
      case "CampaignInitialSendRoiTestCanceledEvent":
        return `ROI test canceled by ${event.data.canceledByUserFullName}.`;
      case "CampaignPartialSendEvaluationCanceledEvent":
        return event.data.reason
          ? `Partial send evaluation canceled: ${event.data.reason}`
          : "Partial send evaluation canceled.";
      case "CampaignInitialSendRoiTestCompletedWithoutWinnerEvent":
        return `ROI test completed without a winner.`;
      case "CampaignPartialSendEvaluationFailedEvent":
        return `Partial send evaluation failed.`;
      case "CampaignInitialSendRoiTestWinnerChosenEvent":
      case "CampaignInitialSendRoiTestSingleVariantPassedEvent":
        return "ROI test completed.";
      case "CampaignPartialSendEvaluationPassedEvent":
        return "Partial send evaluation passed.";
      case "CampaignInitialSendRoiTestSingleVariantFailedEvent":
        return "ROI test completed without reaching conditions to send to full list.";
      case "CampaignMessagingStrategyEditedEvent":
        return "Sending number edited.";
      case "CampaignInitialSendCancelSendingHoldEvent":
        return "Send canceled due to messaging turned off for this account.";
      case "CampaignInitialSendCancelBudgetExceededEvent":
        return "Send canceled due to budget being exceeded for this subaccount.";
      case "CampaignEnableTexterSendEvent":
        return `Texter send enabled by ${event.data.enabledByUserFullName}`;
      case "CampaignDisableTexterSendEvent":
        return `Send canceled by ${event.data.disabledByUserFullName}`;
      case "CampaignTexterSendTimedOutEvent":
        return "Texter send disabled due to lack of activity.";
      case "CampaignTexterSendResumeTextingHoursEvent":
        return "Texter send resumed at the beginning of texting hours.";
      case "CampaignPartialSendEvaluationErrorEvent":
        return "Partial send evaluation encountered an error.";
      case "CampaignInitialSendTMobileCapExceededEvent":
      case "CampaignEventBase":
      case undefined:
        return "Unknown event";
      default:
        event satisfies never;
    }
  }

  return (
    <Table
      rows={sortedEvents.slice(page * rowsPerPage, (page + 1) * rowsPerPage)}
      columns={[
        {
          key: "createdAt",
          title: "Time",
          renderValue: event => new Date(event.createdAt).toLocaleString(),
        },
        {
          key: "message",
          title: "Event",
          align: "left",
          renderValue: renderEventMessage,
        },
      ]}
      onChangePage={setPage}
      onChangeRowsPerPage={setRowsPerPage}
      page={page}
      rowsPerPage={rowsPerPage}
      totalCount={campaign.events.length}
      emptyState="No events for campaign"
    />
  );
}
